import { useEffect, useMemo, useRef } from 'react';
import type { ICatalogRow } from 'app/services/ahuBoardFormsApi';

/**
 * Phase 6e1 — cross-cutting form mechanics (§7.7) as shared utilities.
 */

/**
 * §7.7c — ONE validateNumberField with a clampVsReject mode matching each
 * legacy call site: tab.ahu + int_wall CLAMP to min/max; cabinet/section/
 * dmount MARK INVALID and return ok:false (blocking the set).
 */
export interface NumberFieldOptions {
  min?: number | null;
  max?: number | null;
  mode: 'clamp' | 'reject';
  /** data-step '1' → integer round; '0.01' → 2-decimal round (legacy). */
  step?: number | null;
}

export interface NumberFieldResult {
  ok: boolean;
  value: number | null;
}

export function validateNumberField(
  raw: string,
  { min, max, mode, step }: NumberFieldOptions
): NumberFieldResult {
  const trimmed = raw.replace(/,/g, '').trim();
  if (trimmed === '') return { ok: true, value: null };
  let value = Number(trimmed);
  if (!Number.isFinite(value)) return { ok: false, value: null };
  if (mode === 'clamp') {
    if (min != null && value < min) value = min;
    if (max != null && value > max) value = max;
  } else {
    if (min != null && value < min) return { ok: false, value };
    if (max != null && value > max) return { ok: false, value };
  }
  if (step != null && step >= 1) value = Math.round(value);
  else value = Math.round(value * 100) / 100;
  return { ok: true, value };
}

/**
 * The blur/Enter rule for an UNCONTROLLED decimal box that may carry a lower
 * bound — shared by the two boxes that write an accessory's To Floor
 * (`complexMounting`'s `PositionInput`, `AccessoryMountingPopup`'s `DistInput`;
 * the bound itself comes from `accessDoorFloorClearance`). Returns the text to
 * commit, or null for "nothing to save", and writes a clamped value back into
 * the element on the way:
 *
 *  - an UNTOUCHED box commits nothing. This is load-bearing, not an
 *    optimisation: on a board where the automatic correction is withheld
 *    (view-only, a static version, a project the server reports as not
 *    editable) the stored value sits below the minimum on purpose, and
 *    `pointer-events: none` stops the mouse but not the Tab key — so clamping
 *    on a bare focus-and-tab would save the one value that gate exists to
 *    withhold;
 *  - a typed value below `min` is clamped up to it ('clamp' mode) and written
 *    back into the box, because the element is uncontrolled: a clamp that lands
 *    on the stored value produces no change, no new React `key` and no
 *    re-render, so the rejected text would otherwise stay on screen;
 *  - a clamp that lands back on the stored value commits nothing either.
 *
 * `storedText` is the box's own display of the stored value (`fix`/`fix2` at
 * the call site), so the comparisons stay in the same format the box shows.
 */
export function resolveBoundedCommit(
  el: HTMLInputElement,
  storedText: string,
  min?: number | null
): string | null {
  if (el.value === storedText) return null;
  let next = el.value;
  if (min != null) {
    const checked = validateNumberField(next, { min, mode: 'clamp', step: 0.01 });
    if (checked.ok && checked.value != null) next = checked.value.toFixed(2);
  }
  if (el.value !== next) el.value = next;
  return next === storedText ? null : next;
}

/** PaginatedSelect / blank-off option label = label || model || spn (§7.7b). */
export function catalogLabel(row: ICatalogRow | undefined | null): string {
  if (!row) return '';
  return String(row.label ?? row.model ?? row.spn ?? row.name ?? row.value ?? '');
}

/** number_output data-format="decimal" (uni_tools.changeNumberFormat). */
export function formatDecimal(value: unknown): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value ?? '');
  const fixed = n.toFixed(2);
  const [int, frac] = fixed.split('.');
  return `${int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${frac}`;
}

/**
 * Legacy render-time `validateNumbers()` display formatting for
 * input[data-type="number"][data-step="0.01"]: toFixed(2) + comma separators
 * (tab.ahu.js:622 / tab.section.js:657 / tab.ahu.tunnel.cabinet.js:260).
 * Null/empty stays empty (legacy skips `input.val() == ""`).
 */
export function formatDecimalDisplay(value: unknown): string {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return formatDecimal(n);
}

/**
 * data-formatter_type="integer" display: thousands separators, no decimals
 * (fn.uni_tools.changeNumberFormat — verify 6e2a coil capacity
 * "2,059,576"). Null/empty stays empty.
 */
export function formatIntegerDisplay(value: unknown): string {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * fn.uni_tools.formatNumberWithCommaSeparator (universal_fn_tools.js:973)
 * — faithful port INCLUDING the quirk: `Number(x).toString()` + comma
 * insertion; when NO comma lands (< 1000) the ORIGINAL string is returned
 * (preserving toFixed decimals, e.g. "600.00"), but WITH commas the
 * toString() form wins and trailing zeros drop (1200 → "1,200").
 */
function legacyCommaFormat(value: number | string): string {
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  const s = n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return s.includes(',') ? s : String(value);
}

/**
 * QA5 #2 — legacy validateNumberField DISPLAY formatting (tab.ahu.js:631-663,
 * same copy in tab.section.js / tab.ahu.tunnel.cabinet.js /
 * tab.section.internal_wall.js): data-step == 0.01 → toFixed(2) then the
 * comma quirk ("600.00", "1,200"); anything else → Math.round then the comma
 * quirk ("12,300"). Applied at render AND re-applied on blur/debounce, which
 * is how legacy inputs keep their decimal formatting after focus loss.
 */
export function formatLegacyNumber(value: unknown, step?: number | string | null): string {
  if (value == null || value === '') return '';
  const raw = Number(String(value).replace(/,/g, ''));
  if (!Number.isFinite(raw)) return String(value);
  const decimal = Number(step) === 0.01;
  return decimal ? legacyCommaFormat(raw.toFixed(2)) : legacyCommaFormat(Math.round(raw));
}

/** Debounced callback (legacy 800 ms / 1 s keyup debounces). */
export function useDebouncedCallback<A extends unknown[]>(
  fn: (...args: A) => void,
  delay: number
): (...args: A) => void {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const timer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    []
  );
  return useMemo(
    () =>
      (...args: A) => {
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => fnRef.current(...args), delay);
      },
    [delay]
  );
}

/** Select 'None' → null normalization (§7.7a). */
export function selectValueToId(value: string): number | null {
  if (value === 'None' || value === '') return null;
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * contenteditable remarks serializer — QA5 #6: faithful port of
 * fn.uni_tools.getContenteditableDivHTML (universal_fn_tools.js:935-962).
 * Legacy NEVER persists HTML: divs/brs become newlines, &nbsp; becomes a
 * space, every other tag is stripped, the text is clamped to maxlength (and
 * reflected back into the div as <div>line</div> rows when clamped), one
 * trailing newline is sliced (slice_last=true at the tab.ahu.js:534 call
 * site), and a lone "\n" collapses to ''. Escaped entities other than &nbsp;
 * stay escaped — legacy quirk kept verbatim.
 */
export function getContenteditableHTML(el: HTMLElement, maxLength: number): string {
  const html = el.innerHTML ?? '';
  let text = html
    .replace(/<div><br><\/div>/g, '\n')
    .replace(/<div>/g, '\n')
    .replace(/<\/div>/g, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/&nbsp;/g, ' ')
    .replace(/<[^>]+>/g, '');
  if (text.length > maxLength) {
    text = text.slice(0, maxLength);
    el.innerHTML = text
      .split('\n')
      .map((line) => (line === '' ? '<div><br></div>' : `<div>${line}</div>`))
      .join('');
  }
  if (text.endsWith('\n')) text = text.slice(0, -1);
  return text === '\n' ? '' : text;
}

/**
 * SECTION remarks serializer — legacy tab.section.js:621-631 uses jQuery
 * .text() (textContent, NO div→newline conversion) + maxlength slice, unlike
 * the AHU remarks path above. Kept as a separate helper for parity.
 */
export function getContenteditableText(el: HTMLElement, maxLength: number): string {
  let text = el.textContent ?? '';
  if (text.length >= maxLength) {
    text = text.slice(0, maxLength);
    el.textContent = text;
  }
  return text;
}
